import { BoundFareFamily, BoundWithLocations, FinnairAirBounds, FinnairBoundGroupWithLocation } from '@fcom/booking';
import { isShortHaulFlight } from '@fcom/booking/utils';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { isNotBlank, sortBy } from '@fcom/utils';
import { LocationRouteCffData, RouteCffField } from '@fcom/core-api/index';
import { GlobalBookingTravelClass } from '@fcom/core';

import {
  Cabin,
  FinnairCabinClass,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairLocation,
  FinnairOrder,
  ItineraryItemFlight,
  ItineraryItemLayover,
  OfferList,
} from '../api/models';
import { LocationUtil } from '../interfaces';

const travelClassToCffMap: { [key in GlobalBookingTravelClass]?: RouteCffField } = {
  [GlobalBookingTravelClass.ECONOMY]: 'cffEconomy',
  [GlobalBookingTravelClass.ECOPREMIUM]: 'cffPremiumEconomy',
  [GlobalBookingTravelClass.BUSINESS]: 'cffBusiness',
  [GlobalBookingTravelClass.MIXED]: 'cffMixed',
};

export const getBookingDestinationSummary = (booking: FinnairOrder | FinnairCart): LocationUtil[] => {
  const locations: LocationUtil[] = [];

  booking?.bounds?.forEach((bound) => {
    locations.push(
      {
        code: bound.departure.locationCode,
        name: getCityName(bound.departure.locationCode, booking.locations),
      },
      {
        code: bound.arrival.locationCode,
        name: getCityName(bound.arrival.locationCode, booking.locations),
      }
    );
  });

  return locations;
};

export const getCityName = (locationCode: string, locations: FinnairOrder['locations']): string => {
  const location = locations[locationCode];

  if (location?.cityName) {
    return location.cityName;
  }

  if (location && locations[location.cityCode]) {
    return locations[location.cityCode].cityName;
  }

  return '';
};

const isCabinClassFound = (
  bound: BoundWithLocations | FinnairBoundGroupWithLocation,
  cabinClass: FinnairCabinClass | Cabin
) => {
  return bound.fareFamilies?.find((fareInfo) => {
    return fareInfo.fareInformation.find((fareInformation) => {
      return fareInformation.cabinClass === cabinClass;
    });
  });
};

export const isLongHaulLegAndAy = (
  itineraryArray: (ItineraryItemFlight | ItineraryItemLayover)[],
  location: {
    [key: string]: FinnairLocation;
  }
): boolean => {
  return itineraryArray
    .filter(isFlight)
    .filter((itinerary: FinnairItineraryItemFlight) => itinerary.operatingAirline.code === 'AY')
    .some((itinerary: FinnairItineraryItemFlight) => {
      const departureLocation = itinerary.departure.locationCode;
      const destinationLocation = itinerary.arrival.locationCode;

      const departureCountry = location[departureLocation].countryCode;
      const destinationCountry = location[destinationLocation].countryCode;

      return !isShortHaulFlight(
        { countryCode: departureCountry } as FinnairLocation,
        { countryCode: destinationCountry } as FinnairLocation
      );
    });
};

export const getCabinClassesForBounds = (bounds: FinnairAirBounds): FinnairCabinClass[] => {
  return [FinnairCabinClass.ECONOMY, FinnairCabinClass.ECOPREMIUM, FinnairCabinClass.BUSINESS].filter((cabinClass) => {
    return bounds.boundGroups.find((bound: FinnairBoundGroupWithLocation) => {
      return isCabinClassFound(bound, cabinClass) && isLongHaulLegAndAy(bound.details.itinerary, bounds.locations);
    });
  });
};

export const getCabinClassesForAllBounds = (bounds: FinnairAirBounds): Cabin[] => {
  return [Cabin.ECONOMY, Cabin.ECOPREMIUM, Cabin.BUSINESS, Cabin.MIXED].filter((cabinClass) => {
    return bounds.boundGroups.find((bound: FinnairBoundGroupWithLocation) => {
      return isCabinClassFound(bound, cabinClass);
    });
  });
};

export const getCabinClassesForAwardBounds = (
  bounds: BoundWithLocations[],
  locations: OfferList['locations']
): FinnairCabinClass[] => {
  return [FinnairCabinClass.ECONOMY, FinnairCabinClass.ECOPREMIUM, FinnairCabinClass.BUSINESS].filter((cabinClass) => {
    return bounds.some((bound) => {
      return isCabinClassFound(bound, cabinClass) && isLongHaulLegAndAy(bound.itinerary, locations);
    });
  });
};

/**
 * Sorts the given array of FinnairBoundGroupWithLocation based on the lowest fare family price.
 *
 * @param results The array of FinnairBoundGroupWithLocation to sort.
 * @returns The sorted array of FinnairBoundGroupWithLocation.
 */
export const sortByPrice = <T extends { fareFamilies?: { price: string }[] }>(results: T[]): T[] => {
  return sortBy(
    results.filter((result) => result.fareFamilies && result.fareFamilies.length !== 0),
    (el) => {
      return el.fareFamilies
        .map((ff) => ff.price)
        .reduce<number>((min, price) => {
          const numericPrice = parseInt(price, 10);
          return !min || numericPrice < min ? numericPrice : min;
        }, null);
    }
  );
};

const travelClassAvailableForRoute = (
  travelClass: GlobalBookingTravelClass,
  routeCffData: LocationRouteCffData
): boolean => {
  const cffkey = travelClassToCffMap[travelClass];
  return isNotBlank(routeCffData[cffkey]);
};

export const mapCffsToTravelClasses = (routeCffData: LocationRouteCffData): GlobalBookingTravelClass[] =>
  Object.values(GlobalBookingTravelClass).filter((travelClass) =>
    travelClassAvailableForRoute(travelClass, routeCffData)
  );

export const mapLocationRouteCffDataToCabin = (obj: LocationRouteCffData): Cabin[] => {
  const keys = ['cffMixed', 'cffEconomy', 'cffPremiumEconomy', 'cffBusiness', 'cffFirst'];

  // Define a mapping object for category strings to Cabin values
  const categoryToCabin: { [key: string]: Cabin } = {
    MIXED: Cabin.MIXED,
    ECONOMY: Cabin.ECONOMY,
    PREMIUMECONOMY: Cabin.ECOPREMIUM,
    BUSINESS: Cabin.BUSINESS,
    FIRST: Cabin.FIRST,
  };

  return keys.reduce((result: Cabin[], key: string) => {
    if (Object.prototype.hasOwnProperty.call(obj, key) && key.startsWith('cff') && obj[key]) {
      const category = (key.replace('cff', '').charAt(0).toUpperCase() + key.slice(4)).toUpperCase();
      const cabin = categoryToCabin[category];
      if (cabin) {
        result.push(cabin);
      }
    }
    return result;
  }, []);
};

export const getFareFamiliesLowestPriceOrPoints = (
  fareFamilies: BoundFareFamily[],
  isAward: boolean
): number | null | undefined => {
  return fareFamilies?.reduce<number>((min, ff) => {
    const numericPrice = isAward ? parseInt(ff.points) : parseFloat(ff.price);
    return !min || numericPrice < min ? numericPrice : min;
  }, null);
};
